import { useNavigate, useLocation } from 'react-router-dom';
import { useContext } from 'react';
import { useCOAProcessing } from '@/contexts/COAProcessingContext';
import { CheckCircle2, AlertTriangle, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useFeatureRegistration, type FeatureMeta } from '@/contexts/FeatureRegistryContext';

export const FEATURE_META: FeatureMeta = {
  id: 'coa-processing-banner',
  label: 'COA Processing Banner',
  description: 'Persistent banner showing live status of async COA extraction jobs (queued, processing, completed, failed).',
  triggerType: 'panel',
  personas: ['seller', 'buyer_seller'],
};

export default function COAProcessingBanner() {
  useFeatureRegistration(FEATURE_META);
  // Gracefully handle missing provider (e.g. during HMR)
  let ctx: ReturnType<typeof useCOAProcessing> | null = null;
  try {
    ctx = useCOAProcessing();
  } catch {
    return null;
  }

  const { bannerJob, dismissBanner, bannerSuggestionCount, pendingConfirm } = ctx;
  const navigate = useNavigate();
  const location = useLocation();

  // Pending strain-mismatch decision takes precedence when the user is NOT
  // already on the listing's edit page. Once they navigate there, the global
  // `COAConfirmDialog` opens automatically.
  if (pendingConfirm) {
    const expectedPath = `/listings/${pendingConfirm.listingId}/edit`;
    if (location.pathname !== expectedPath) {
      return (
        <div className="fixed top-0 left-0 right-0 z-[60] transition-transform duration-300 ease-out border-b bg-amber-50 text-amber-900 border-amber-200 dark:bg-amber-950/50 dark:text-amber-100 dark:border-amber-800">
          <div className="container-wide py-3 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <AlertTriangle className="w-5 h-5 shrink-0" />
              <p className="text-sm font-medium truncate">
                COA strain doesn't match the listing — review needed.
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Button
                variant="outline"
                size="sm"
                className="border-amber-300 text-amber-900 hover:bg-amber-100 dark:border-amber-700 dark:text-amber-100 dark:hover:bg-amber-900"
                onClick={() => navigate(expectedPath)}
              >
                Review
              </Button>
            </div>
          </div>
        </div>
      );
    }
    // On edit page — dialog will handle it; don't double-up with a banner.
    return null;
  }

  if (!bannerJob) return null;

  const { listingId, result } = bannerJob;
  const isError = result.status === 'failed';

  let summaryText: string;
  if (isError) {
    summaryText = result.message || 'COA processing failed. Please try again.';
  } else if (bannerSuggestionCount > 0) {
    summaryText = `COA processed — ${bannerSuggestionCount} suggestion${bannerSuggestionCount === 1 ? '' : 's'} to review.`;
  } else {
    const fieldsText = result.coa_fields_extracted?.length
      ? result.coa_fields_extracted.join(', ')
      : 'lab data';
    summaryText = `COA processed — extracted ${fieldsText}.`;
  }

  const handleViewListing = () => {
    dismissBanner();
    navigate(`/listings/${listingId}/edit`, { state: { scrollToSuggestions: true } });
  };

  return (
    <div
      className={`fixed top-0 left-0 right-0 z-[60] transition-transform duration-300 ease-out border-b ${
        isError
          ? 'bg-destructive/10 text-destructive border-destructive/20'
          : 'bg-emerald-50 text-emerald-900 border-emerald-200 dark:bg-emerald-950/50 dark:text-emerald-100 dark:border-emerald-800'
      }`}
    >
      <div className="container-wide py-3 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          {isError ? (
            <AlertTriangle className="w-5 h-5 shrink-0" />
          ) : (
            <CheckCircle2 className="w-5 h-5 shrink-0" />
          )}
          <p className="text-sm font-medium truncate">{summaryText}</p>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {!isError && (
            <Button
              variant="outline"
              size="sm"
              className="border-emerald-300 text-emerald-800 hover:bg-emerald-100 dark:border-emerald-700 dark:text-emerald-200 dark:hover:bg-emerald-900"
              onClick={handleViewListing}
            >
              {bannerSuggestionCount > 0 ? 'Review Suggestions' : 'View Listing'}
            </Button>
          )}
          <Button variant="quiet" size="icon-sm" type="button"
            onClick={dismissBanner}
            className="shrink-0 p-1 rounded-md hover:bg-black/10 dark:hover:bg-white/10 transition-colors"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
